// Unpublished Work © 2021-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import EditableCell from 'Ui/components/common/data-table/editable-cell';
import SwitchInput from 'Ui/components/common/form/switch-input';
import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {fetchEffectData} from 'Utils/react-utils';
import {getFilteredFleetEquipment} from 'Services/membership-service';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {bulkUpdateEquipment} from 'Utils/equipment-utils';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {dateCompare} from 'Ui/models/maintenance';
import {formatTimeOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {MAX_ENGINE_HOURS} from 'Ui/constants/equipment-constants';
import {translateEquipmentArea} from 'Utils/translation-utils';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {Box} from '@deere/isg.component-library';

const TEN = 10;

function parseToTenths(value) {
    return Math.floor(parseFloat(value) * TEN) / TEN;
}

function getDataGridColumns(translations, updateEngineHours, setUpdateEngineHours, setValid, currentFleet, setCurrentFleet) {
    return [
        {
            headerName: translations.NAME,
            minWidth: 130,
            flex: 1,
            field: 'equipmentName'
        },
        {
            headerName: translations.TYPE,
            minWidth: 130,
            flex: 1,
            field: 'equipmentType'
        },
        {
            headerName: translations.BRAND,
            minWidth: 130,
            flex: 1,
            field: 'manufacturerName'
        },
        {
            headerName: translations.MODEL,
            minWidth: 130,
            flex: 1,
            field: 'modelName'
        },
        {
            headerName: translations.ONLINK_SERIAL_NUMBER,
            minWidth: 130,
            flex: 1,
            field: 'serialNumber'
        },
        {
            headerName: translations.ONLINK_LAST_ENGINE_HOURS_UPDATE,
            field: 'timeLastMeterReading',
            width: 255,
            sortComparator: dateCompare,
            renderCell: (params) => {
                return params.row.formattedTimeLastMeterReading;
            }
        },
        {
            headerName: translations.HOURS,
            field: 'totalHours',
            align: 'right',
            headerAlign: 'right',
            width: 110,
            renderCell: (params) => {
                const {row: equipment} = params;

                function onChange(event) {
                    const {value} = event.target;
                    const parsedValue = value ? parseToTenths(value) : '';

                    const index = updateEngineHours.findIndex((equipmentEntry) => equipmentEntry.equipmentId === equipment.equipmentId);
                    const fleetIndex = currentFleet.findIndex((equipmentEntry) => equipmentEntry.equipmentId === equipment.equipmentId);

                    currentFleet[fleetIndex].totalHours = parsedValue;

                    if (index !== -1) {
                        updateEngineHours[index].totalHours = parsedValue;
                    } else {
                        updateEngineHours.push({
                            equipmentId: equipment.equipmentId,
                            equipmentName: equipment.equipmentName,
                            totalHours: parsedValue
                        });
                    }

                    setCurrentFleet(currentFleet);
                    setUpdateEngineHours(updateEngineHours);

                    return parsedValue;
                }

                return (
                    <Box
                        alignItems='center'
                        display='flex'
                        height='100%'
                        justifyContent='center'
                        mt='0.5rem'
                        width='100%'
                    >
                        <EditableCell
                            errors={{
                                rangeUnderflow: ' ',
                                rangeOverflow: ' '
                            }}
                            initialValue={parseToTenths(equipment.totalHours)}
                            max={MAX_ENGINE_HOURS}
                            min={0}
                            name={`engineHours${params.row.equipmentId}`}
                            onChange={onChange}
                            setValid={setValid}
                            type='number'
                        />
                    </Box>
                );
            }
        }
    ];
}

function getFilterComponent(showTelematicEnabled, setShowTelematicEnabled, translations) {
    return (
        <div className='extra-table-options'>
            <SwitchInput
                checked={showTelematicEnabled}
                name='showTelematicEnabled'
                offLabel={translations.ONLINK_SHOW_TELEMATIC_ENABLED}
                onChange={() => setShowTelematicEnabled(!showTelematicEnabled)}
                onLabel={translations.ONLINK_SHOW_TELEMATIC_ENABLED}
                translations={translations}
            />
        </div>
    );
}

function initializeState() {
    const [showTelematicEnabled, setShowTelematicEnabled] = React.useState(() => false);
    const [currentFleet, setCurrentFleet] = React.useState(() => []);
    const [updateEngineHours, setUpdateEngineHours] = React.useState(() => []);
    const [loading, setLoading] = React.useState(() => false);
    const [footerLoading, setFooterLoading] = React.useState(() => false);

    return {
        showTelematicEnabled,
        setShowTelematicEnabled,
        currentFleet,
        setCurrentFleet,
        updateEngineHours,
        setUpdateEngineHours,
        loading,
        setLoading,
        footerLoading,
        setFooterLoading
    };
}

function BulkEngineHoursDialog(props) {
    const {
        addToast,
        closeDialog,
        invalidInputs,
        membershipId,
        setValid,
        title,
        translations,
        updateFleetEquipment
    } = props;

    const {
        showTelematicEnabled,
        setShowTelematicEnabled,
        currentFleet,
        setCurrentFleet,
        updateEngineHours,
        setUpdateEngineHours,
        loading,
        setLoading,
        footerLoading,
        setFooterLoading
    } = initializeState();

    async function fetchFleetData(isMounted) {
        setLoading(true);

        const equipment = await getFilteredFleetEquipment();

        const nonArchivedEquipment = equipment
            .map((nonArchived) => ({
                ...nonArchived,
                equipmentArea: translateEquipmentArea(translations, nonArchived.equipmentArea) || translations.ONLINK_UNASSIGNED,
                formattedTimeLastMeterReading: formatTimeOrDefault(nonArchived.timeLastMeterReading)
            }));

        if (isMounted()) {
            setCurrentFleet(nonArchivedEquipment);
            setUpdateEngineHours([]);
            setLoading(false);
        }
    }

    async function addErrorMessage(rejectedNames) {
        const message = translations.ONLINK_BULK_HOURS_ERROR.replace('{0}', rejectedNames);

        addToast({
            message,
            persist: true,
            type: TOAST_TYPE.ERROR
        });

        await fetchFleetData(() => true);
    }

    async function onSave() {
        setFooterLoading(true);

        const rejectedResults = await bulkUpdateEquipment(updateEngineHours);

        setFooterLoading(false);

        if (rejectedResults.length !== 0) {
            const rejectedNames = rejectedResults.join(', ');

            await addErrorMessage(rejectedNames);
        } else {
            updateFleetEquipment();
            closeDialog();
        }
    }

    React.useEffect(() => fetchEffectData(fetchFleetData), [membershipId]);

    const filteredFleet = showTelematicEnabled ?
        currentFleet :
        currentFleet.filter((equipment) => !equipment.lastReportedMtgId);

    return (
        <FormDialog
            addToast={addToast}
            cancelLabel={translations.CANCEL}
            className='bulk-engine-hours-dialog'
            closeHandler={closeDialog}
            disableSave={invalidInputs.size > 0}
            footerLoading={footerLoading}
            hasRequiredFields={false}
            onSave={onSave}
            saveLabel={translations.save}
            title={title}
            translations={translations}
            width={'lg'}
        >
            <div className='data-grid'>
                <DataGridWrapper
                    columns={getDataGridColumns(translations, updateEngineHours, setUpdateEngineHours, setValid, currentFleet, setCurrentFleet)}
                    filterComponent={getFilterComponent(showTelematicEnabled, setShowTelematicEnabled, translations)}
                    getRowId={(row) => row.equipmentId}
                    initialState={{
                        sorting: {
                            sortModel: [
                                {
                                    field: 'equipmentName',
                                    sort: 'asc'
                                }
                            ]
                        }
                    }}
                    loading={loading}
                    rows={filteredFleet}
                    searchFields={['equipmentName', 'equipmentType', 'manufacturerName', 'modelName', 'serialNumber', 'formattedTimeLastMeterReading']}
                    searchable={true}
                    slotProps={{
                        root: {
                            disableColumnMenu: true
                        }
                    }}
                    translations={translations}
                /></div>
        </FormDialog>
    );
}

BulkEngineHoursDialog.propTypes = {
    addToast: PropTypes.func,
    closeDialog: PropTypes.func,
    invalidInputs: PropTypes.instanceOf(Set),
    membershipId: PropTypes.string,
    setValid: PropTypes.func,
    title: PropTypes.string,
    translations: PropTypes.translations,
    updateFleetEquipment: PropTypes.func
};

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.BULK_ENGINE_HOURS_DIALOG));
        }
    };
}

export default connect(null, mapDispatchToProps)(FormValidator(BulkEngineHoursDialog));
